import dayjs from "dayjs";

/** Enum of RewardClaim statuses, mirroring backend's `REWARD_CLAIM_STATUS_ENUM` (reward-claim.js). */
export const REWARD_CLAIM_STATUS_ENUM = Object.freeze({
  PENDING_VERIFICATION: "PENDING_VERIFICATION",
  VERIFIED: "VERIFIED",
  PACKED: "PACKED",
  SHIPPED: "SHIPPED",
  DELIVERED: "DELIVERED",
  REJECTED: "REJECTED",
  BLOCKED: "BLOCKED"
});

/** Active statuses that can still move forward, in fulfilment order. */
export const IN_PROGRESS_CLAIM_STATUSES = Object.freeze([
  REWARD_CLAIM_STATUS_ENUM.PENDING_VERIFICATION,
  REWARD_CLAIM_STATUS_ENUM.VERIFIED,
  REWARD_CLAIM_STATUS_ENUM.PACKED,
  REWARD_CLAIM_STATUS_ENUM.SHIPPED
]);

/** Active statuses that are terminal. */
export const FULFILLED_CLAIM_STATUSES = Object.freeze([REWARD_CLAIM_STATUS_ENUM.DELIVERED]);

/** Every active status: in-progress first, then fulfilled. Order is the forward direction. */
export const ACTIVE_CLAIM_STATUSES = Object.freeze([
  ...IN_PROGRESS_CLAIM_STATUSES,
  ...FULFILLED_CLAIM_STATUSES
]);

/**
 * Returns the targets the update-status endpoint accepts for a claim in `currentStatus`.
 *  - An in-progress claim moves to any later active status, or to REJECTED or BLOCKED.
 *    It never moves backward.
 *  - A REJECTED claim moves to PENDING_VERIFICATION (reactivation) or BLOCKED.
 *  - A DELIVERED claim has no moves, and a BLOCKED claim leaves only through the dedicated
 *    unblock action, so both return an empty list.
 *
 * @param {string} currentStatus
 * @returns {Array<string>} Empty for a terminal, blocked or absent status.
 */
export const getAllowedNextClaimStatuses = (currentStatus) => {
  if (IN_PROGRESS_CLAIM_STATUSES.includes(currentStatus)) {
    const laterActiveStatuses = ACTIVE_CLAIM_STATUSES.slice(
      ACTIVE_CLAIM_STATUSES.indexOf(currentStatus) + 1
    );
    return [
      ...laterActiveStatuses,
      REWARD_CLAIM_STATUS_ENUM.REJECTED,
      REWARD_CLAIM_STATUS_ENUM.BLOCKED
    ];
  }
  if (currentStatus === REWARD_CLAIM_STATUS_ENUM.REJECTED) {
    return [REWARD_CLAIM_STATUS_ENUM.PENDING_VERIFICATION, REWARD_CLAIM_STATUS_ENUM.BLOCKED];
  }
  return [];
};

/** Statuses that require a non-empty `adminActionReason` when set via update-status. */
export const CLAIM_STATUS_REQUIRING_REASON = Object.freeze([
  REWARD_CLAIM_STATUS_ENUM.REJECTED,
  REWARD_CLAIM_STATUS_ENUM.BLOCKED
]);

/** AntD `Tag` colors per status, for the claims table and detail modal. */
export const CLAIM_STATUS_TAG_COLORS = Object.freeze({
  [REWARD_CLAIM_STATUS_ENUM.PENDING_VERIFICATION]: "gold",
  [REWARD_CLAIM_STATUS_ENUM.VERIFIED]: "blue",
  [REWARD_CLAIM_STATUS_ENUM.PACKED]: "cyan",
  [REWARD_CLAIM_STATUS_ENUM.SHIPPED]: "geekblue",
  [REWARD_CLAIM_STATUS_ENUM.DELIVERED]: "green",
  [REWARD_CLAIM_STATUS_ENUM.REJECTED]: "red",
  [REWARD_CLAIM_STATUS_ENUM.BLOCKED]: "volcano"
});

/**
 * Formats a `SCREAMING_SNAKE_CASE` enum value into a human-readable label.
 * Returns an empty string for an absent value, so a missing status renders blank.
 *
 * @param {string} [value=""] - The enum value.
 * @returns {string} Title-cased words separated by spaces.
 * @example formatEnumLabel("PENDING_VERIFICATION") // "Pending Verification"
 */
export const formatEnumLabel = (value = "") => {
  return value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Formats a reward claim's `items` list into a comma-separated string of
 * human-readable item key labels, for the claims table and detail modal.
 *
 * @param {Array<{itemKey: string}>} [items=[]] - The claim's item snapshot.
 * @returns {string} The labels joined by `", "`, or `"-"` when the claim has no items.
 * @example formatItemKeysList([{ itemKey: "T_SHIRT" }]) // "T Shirt"
 */
export const formatItemKeysList = (items = []) =>
  items.map(({ itemKey }) => formatEnumLabel(itemKey)).join(", ") || "-";

const CLAIMS_CSV_HEADERS = Object.freeze([
  "Course Name",
  "Course URL Identifier",
  "Status",
  "Items",
  "Full Name",
  "Email",
  "Phone",
  "Address Line 1",
  "Address Line 2",
  "City",
  "State",
  "Pincode",
  "Country",
  "Claim Attempts",
  "Created At"
]);

/**
 * Builds the CSV text for a list of reward claims, one row per claim under a fixed header.
 * Every text field is quoted, and a field that starts with a formula character
 * (`=`, `+`, `-`, `@`) is prefixed with `'` so a spreadsheet does not evaluate it, except a `+`
 * followed only by digits, spaces or hyphens, which is a phone number. Items are
 * joined with `" | "` inside one cell.
 *
 * @param {Array<object>} claims - Claims as returned by the export endpoint.
 * @returns {string} The full CSV document, rows separated by `"\n"`.
 */
export const buildClaimsCsv = (claims) => {
  const csvRows = claims.map((claim) => {
    const {
      courseName = "",
      courseUrlIdentifier = "",
      status = "",
      items = [],
      shippingDetails = {},
      claimAttempts = 1,
      createdAt = ""
    } = claim || {};
    const {
      fullName = "",
      email = "",
      phone = "",
      addressLine1 = "",
      addressLine2 = "",
      city = "",
      state = "",
      pincode = "",
      country = ""
    } = shippingDetails || {};
    const itemsLabel = items.map(({ itemKey }) => itemKey).join(" | ");

    return [
      escapeCsvField(courseName),
      escapeCsvField(courseUrlIdentifier),
      escapeCsvField(status),
      escapeCsvField(itemsLabel),
      escapeCsvField(fullName),
      escapeCsvField(email),
      escapeCsvField(phone),
      escapeCsvField(addressLine1),
      escapeCsvField(addressLine2),
      escapeCsvField(city),
      escapeCsvField(state),
      escapeCsvField(pincode),
      escapeCsvField(country),
      claimAttempts,
      createdAt ? dayjs(createdAt).toISOString() : ""
    ].join(",");
  });

  return [CLAIMS_CSV_HEADERS.join(","), ...csvRows].join("\n");
};

/**
 * Saves CSV text as a file download in the browser.
 *
 * @param {string} csvContent - The CSV document.
 * @param {string} fileName - Name of the downloaded file.
 */
export const downloadCsvFile = (csvContent, fileName) => {
  const csvBlob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const downloadUrl = window.URL.createObjectURL(csvBlob);
  const downloadLink = document.createElement("a");
  downloadLink.href = downloadUrl;
  downloadLink.setAttribute("download", fileName);
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
  window.URL.revokeObjectURL(downloadUrl);
};

/** A `+` followed only by digits, spaces or hyphens: a phone number, never a formula. */
const INTERNATIONAL_PHONE_PATTERN = /^\+[\d\s-]+$/;

/**
 * Quotes one CSV field. A null or undefined field becomes an empty quoted string, line breaks
 * normalise to `"\n"`, embedded quotes double, and a leading formula character is neutralised.
 * An international phone number such as `+919876543210` is left as is.
 *
 * @param {*} field - The raw cell value.
 * @returns {string} The quoted field.
 */
const escapeCsvField = (field) => {
  if (field === null || field === undefined) {
    return '""';
  }
  let stringifiedField = String(field).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const isInternationalPhoneNumber = INTERNATIONAL_PHONE_PATTERN.test(stringifiedField);
  if (!isInternationalPhoneNumber && /^[\t\r\n\f\v ]*[=+\-@]/.test(stringifiedField)) {
    stringifiedField = `'${stringifiedField}`;
  }
  return `"${stringifiedField.replace(/"/g, '""')}"`;
};
